import { useCallback, useReducer, useContext, useEffect } from 'react';
import axios from 'axios';
import { apiExpress, options } from 'lib/config';
import { ActionDispatchContext } from 'lib/ActionDispatchContext';

import {
  ActionType,
  requestActionCreator,
  successActionCreator,
  failureActionCreator,
} from '../actionCreators';

export interface LinksetsData {
  id: string;
  title: string;
  defaultLinks: number;
  company?: number;
}

interface LinksetsStore {
  data: Array<LinksetsData>;
  loading: boolean;
  error: string | null;
  invalidate(): void;
}

interface LinksetsStoreState {
  data: Array<LinksetsData> | undefined;
  error: string | null;
  loading: boolean;
  isValid: boolean;
}

function reducer(state: LinksetsStoreState, action: ActionType) {
  switch (action.type) {
    case 'INVALIDATE':
      return {
        ...state,
        isValid: false,
      };

    case 'FETCH_REQUEST':
      return {
        ...state,
        loading: true,
        error: null,
      };

    case 'FETCH_SUCCESS':
      return {
        ...state,
        data: action.payload,
        loading: false,
        error: null,
        isValid: true,
      };

    case 'FETCH_FAILURE':
      return {
        ...state,
        data: undefined,
        loading: false,
        error: action.payload,
        isValid: true,
      };

    default:
      return state;
  }
}

const useActionListener = (dispatch: Function) => {
  const { action } = useContext(ActionDispatchContext);

  useEffect(() => {
    dispatch(action);
  }, [action, dispatch]);
};

export const useLinksetsStore = (): LinksetsStore => {
  const initialState = {
    data: [] as Array<LinksetsData>,
    loading: false,
    error: null,
    isValid: false,
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const { data, error, loading, isValid } = state;

  useActionListener(dispatch);

  useEffect(() => {
    if (isValid) {
      return;
    }
    const fetchData = async () => {
      dispatch(
        requestActionCreator({
          type: 'FETCH_REQUEST',
        })
      );

      axios
        .get(`${apiExpress}exitlinksets`, {
          headers: options.bearerToken,
        })
        .then(response => {
          const { linksets } = response.data;

          linksets.sort((setA: any, setB: any) => {
            return setB.defaultLinks - setA.defaultLinks - setB.company;
          });

          dispatch(
            successActionCreator({
              type: 'FETCH_SUCCESS',
              payload: linksets,
            })
          );
        })
        .catch(err => {
          // Axios automatically throws an error in case of HTTP or Network errors
          // so we can eventually put error parsing here and return whatever value we want based on that.
          dispatch(
            failureActionCreator({
              type: 'FETCH_FAILURE',
              payload: err.message,
            })
          );
        });
    };
    fetchData();
  }, [isValid]);

  const invalidate = useCallback(() => dispatch({ type: 'INVALIDATE' }), []);

  return {
    data,
    loading,
    error,
    invalidate,
  };
};
